import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../contexts/auth/Context";
import { useCurrentGym } from "./useCurrentGym";
import { uploadToS3Fn } from "../user/avatar/s3-upload";
import { updateGymLogoRequest } from "../requests/gym";
import { deleteS3ObjectRequest } from "../requests/aws/s3";
import { compressImageForUploadDual } from "../../utils/imageCompression";

const CURRENT_GYM_QUERY_KEY = ["gym", "current"] as const;

export const useUploadGymLogo = () => {
  const { token } = useAuth();
  const { data: gym } = useCurrentGym();
  const queryClient = useQueryClient();
  const [isCompressing, setCompressing] = useState(false);

  const mutation = useMutation({
    mutationFn: async (file: File) => {
      if (!token) throw new Error("Sesión expirada o no válida");
      if (!gym?.publicId) throw new Error("No se encontró el negocio");

      setCompressing(true);
      let webp: File;
      let jpeg: File;
      try {
        const dual = await compressImageForUploadDual(file);
        webp = dual.webp;
        jpeg = dual.jpeg;
      } finally {
        setCompressing(false);
      }

      const [uploadWebp, uploadJpeg] = await Promise.all([
        uploadToS3Fn(webp, token, "gyms", gym.publicId, "logo"),
        uploadToS3Fn(jpeg, token, "gyms", gym.publicId, "logo"),
      ]);

      if (!uploadWebp.success || !uploadWebp.key) {
        throw new Error(uploadWebp.error || "Error al subir archivo");
      }
      if (!uploadJpeg.success || !uploadJpeg.key) {
        throw new Error(uploadJpeg.error || "Error al subir archivo");
      }

      await updateGymLogoRequest(uploadWebp.key, token, uploadJpeg.key);

      if (gym.logoKey && gym.logoKey !== uploadWebp.key) {
        await deleteS3ObjectRequest(gym.logoKey, token);
      }
      if (gym.logoKeyEmail && gym.logoKeyEmail !== uploadJpeg.key) {
        await deleteS3ObjectRequest(gym.logoKeyEmail, token);
      }

      return uploadWebp;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CURRENT_GYM_QUERY_KEY });
    },
  });

  return {
    uploadLogo: mutation.mutateAsync,
    isCompressing,
    isUploading: mutation.isPending,
    error: mutation.error instanceof Error ? mutation.error.message : null,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
  };
};
